/**
 *  Filename ....: security.component.ts
 *  Created by ..: ianday
 *  on Date .....: 7/9/25
**/
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { NodeService } from '../../../services/node.service';
import { filter } from 'rxjs/operators';
import { SecurityService } from '../../../services/security.service';

@Component({
	selector: 'app-security',
	templateUrl: './security.component.html',
	styleUrls: ['./security.component.css'],
	encapsulation: ViewEncapsulation.None
})
export class SecurityComponent implements OnInit {

	private calledFrom = '';
	private parentId = 0;
	public strErr = '';
	public isEditing = true;
	public pagination = 'Record(s) ...';
	public valComponent: string;
	public valDescription: string;
	public valActiveStatus: string;
	public valUsedInMultipleAreas: boolean;
	public valType: string;
	public expanded = false;
	public Idx = -1;
	public selectedTarget: any;

	public files!: TreeNode[];
	public origFiles!: TreeNode[];
	public cols!: ['field'];

	public filterMode = 'lenient';

	public rightTypeMap = [];

	public dummyValue = 'No Access';

	@Output() cancelButton = new EventEmitter();
	@Output() submitButton = new EventEmitter();

	@Input() set CalledFrom(value: string) {
		this.calledFrom = value;
	}

	@Input() set ParentId(value: number) {
		this.parentId = value;
	}
	constructor(public nodeService: NodeService, public securityService: SecurityService) {
		this.nodeService.getFilesystem().then((files) => {
			this.origFiles = files;
		});
	}

	ngOnInit() {}

	public setPagination(totalVal) {
		this.pagination = totalVal + ' Record(s) ...';
	}

	public loadSecurityTargets() {
		this.rightTypeMap = [];
		this.securityService.getSecurityTargetElementsJSON().subscribe(
			data => {
				if (data.length === 0) {
					return;
				}
				this.files = this.sortTreeNodesByChildPresence(data);

				if (this.calledFrom === 'groupMgmt') {
					this.securityService.getSecurityTargetElementsBySecurityGroup(this.parentId).subscribe(
						(grpData) => {
							this.rightTypeMap = grpData;
							this.mapRightsToSecurityTargets();
							this.origFiles = JSON.parse(JSON.stringify(this.files)); // clone for filtering
						},
						(err) => {
							console.log(err);
						});
				}
				else {
					this.securityService.getSecurityTargetElementsByUser(this.parentId).subscribe(
						(userData) => {
							this.rightTypeMap = userData;
							this.mapRightsToSecurityTargets();
							this.origFiles = JSON.parse(JSON.stringify(this.files)); // clone for filtering
						},
						(err) => {
							console.log(err);
						});
				}
			},
			error => {
				console.log(error);
			});
	}

	public mapRightsToSecurityTargets(): void {
		if (this.rightTypeMap.length > 0) {
			this.traverseTreeSetRights(this.files);
		}
	}

	/**
	 * Recursively sorts tree nodes so that leaf nodes (items without children) appear before
	 * branch nodes (items with children). This improves visibility of individual items.
	 */
	public sortTreeNodesByChildPresence(nodes: TreeNode[]): TreeNode[] {
		if (!nodes || nodes.length === 0) return nodes;

		// Sort nodes: items without children come first, items with children come last
		const sorted = nodes.sort((a, b) => {
			const aHasChildren = a.children && a.children.length > 0;
			const bHasChildren = b.children && b.children.length > 0;

			// If one has children and the other doesn't, the one without comes first
			if (aHasChildren && !bHasChildren) return 1;
			if (!aHasChildren && bHasChildren) return -1;
			// If both have same child status, maintain original order
			return 0;
		});

		// Recursively sort children
		sorted.forEach(node => {
			if (node.children && node.children.length > 0) {
				node.children = this.sortTreeNodesByChildPresence(node.children);
			}
		});

		return sorted;
	}

	public traverseTreeSetRights(nodes: TreeNode[]): void {
		if (!nodes) return;

		for (const node of nodes) {
			const rightType = this.rightTypeMap.find(f => f.id === node.data.id)?.rightType;

			if (rightType !== undefined) {
				node.data.rightType = rightType;
				node.data.rightTypeLabel = this.labelerDescription(rightType);
			}
			if (node.children && node.children.length > 0) {
				this.traverseTreeSetRights(node.children);
			}
		}
	}

	public mapSecurityTargetsToRights(): void {
		this.rightTypeMap = [];
		this.traverseTreeGetRights(this.files);
	}

	public traverseTreeGetRights(nodes: TreeNode[]): void {
		if (!nodes) return;

		for (const node of nodes) {
			if (node.data.rightType !== 'No Access') {
				this.rightTypeMap.push({
					id: node.data.id,
					name: node.data.name,
					code: node.data.code,
					parentId: node.data.parentId,
					rightType: node.data.rightType,
					rightTypeLabel: this.labelerDescription(node.data.rightType)
				});
			}
			if (node.children && node.children.length > 0) {
				this.traverseTreeGetRights(node.children);
			}
		}
	}

	public applyFilter(filterVal: string): void {
		const q = (filterVal || '').toString().trim().toLowerCase();

		if (!q) {
			this.files = JSON.parse(JSON.stringify(this.origFiles || []));
			this.collapseAll();
			return;
		}

		const filterRecursive = (nodes: any[]): any[] => {
			if (!nodes || nodes.length === 0) return [];
			const out: any[] = [];

			for (const n of nodes) {
				const name = (n.data?.name || '').toString().toLowerCase();
				const label = (n.data?.rightTypeLabel || '').toString().toLowerCase();
				const nodeMatches = name.includes(q) || label.includes(q);

				const matchedChildren = n.children ? filterRecursive(n.children) : [];

				if (nodeMatches) {
					out.push({
						data: n.data,
						children: matchedChildren,
						expanded: true
					});
				} else if (matchedChildren.length > 0) {
					out.push({
						data: n.data,
						children: matchedChildren,
						expanded: true
					});
				}
			}
			return out;
		};

		const filtered = filterRecursive(this.origFiles || []);
		this.files = this.sortTreeNodesByChildPresence(filtered);

		if (this.selectedTarget && !this._nodeExists(this.files, this.selectedTarget.data?.id)) {
			this.selectedTarget = null;
		}
	}

	private _nodeExists(nodes: any[], id: any): boolean {
		if (!nodes) return false;
		for (const n of nodes) {
			if (n.data && n.data.id === id) return true;
			if (n.children && this._nodeExists(n.children, id)) return true;
		}
		return false;
	}

	public queryRecords(): void {}

	public submitRecord(): void {
		this.mapSecurityTargetsToRights();

		if (this.calledFrom === 'groupMgmt') {
			this.securityService.updateSecurityTargetElementsbySecurityGroup(this.parentId, this.rightTypeMap).subscribe(
				data => {
					this.submitButton.emit(this.parentId);
				},
				error => {
					console.log(error);
				});
		}
		else {
			this.securityService.updateSecurityTargetElementsbyUser(this.parentId, this.rightTypeMap).subscribe(
				data => {
					const csvList = this.rightTypeMap
						.map(item => item.name)
						.join(', ');
					this.submitButton.emit(csvList);
				},
				error => {
					console.log(error);
				});
		}
	}

	public fetchAccessOptions(show, execute, readonly, readwrite): string {
		if (readwrite === true) return 'readWrite';
		if (readonly === true) return 'readOnly';
		if (execute === true) return 'execute';
		if (show === true) return 'show';
		return '';
	}

	public expandAll(): void {
		if (this.expanded === true) return;

		this.expanded = true;
		this.files.forEach(node => {
			this.expandCollapseRecursive(node, this.expanded);
		});
	}

	public collapseAll(): void {
		if (this.expanded === false) return;

		this.expanded = false;
		this.files.forEach(node => {
			this.expandCollapseRecursive(node, this.expanded);
		});
	}

	private expandCollapseRecursive(node: TreeNode, isExpand: boolean): void {
		node.expanded = isExpand;
		if (node.children) {
			node.children.forEach(childNode => {
				this.expandCollapseRecursive(childNode, isExpand);
			});
			this.files = [...this.files];
		}
	}

	public cancelSubmitRecord(): void {
		this.cancelButton.emit();
	}

	public onNodeSelect(event) {
		this.valComponent = this.selectedTarget.data.name;
		this.valDescription = this.selectedTarget.data.description;
		this.valType = this.selectedTarget.data.elementType;
		this.valActiveStatus = this.selectedTarget.data.activeStatus;
		this.valUsedInMultipleAreas = this.selectedTarget.data.usedInMultipleAreas;
	}

	public onNodeUnselect(event) {}

	public labelerDescription(rightType: string): string {
		switch (rightType) {
			case 'EXECUTE': return 'Execute';
			case 'READ': return 'Read Only';
			case 'WRITE': return 'Read Write';
			case 'SHOW': return 'Show';
			default: return 'No Access';
		}
	}

	public showAccessColor(rightType: string): any {
		if (rightType === undefined || rightType === '') return 'silver';
		switch (rightType.toLowerCase()) {
			case 'no access': return 'silver';
			case 'read': return 'green';
			case 'write': return 'green';
			case 'show': return 'purple';
			case 'execute': return 'darkorange';
		}
		return 'darkgray';
	}

	protected readonly filter = filter;
}
